import React, { useRef, useState, useEffect, useCallback } from 'react';

/**
 * SwipeActionRow — wraps a row and reveals action buttons when the user
 * swipes left on it. Designed for touch-first mobile list items.
 *
 * Props:
 *   actions: [{ icon: Component, label: string, color: 'green'|'red'|'blue'|'amber', onClick: () => void }]
 *   children: the row content
 *   testId: optional data-testid
 */
const COLOR_CLASSES = {
  green: 'bg-emerald-500 text-white',
  red: 'bg-red-500 text-white',
  blue: 'bg-blue-500 text-white',
  amber: 'bg-amber-500 text-white',
  primary: 'bg-teal-600 text-white',
};

export const SwipeActionRow = ({ actions = [], children, testId }) => {
  const rowRef = useRef(null);
  const startX = useRef(null);
  const currentDelta = useRef(0);
  const [translateX, setTranslateX] = useState(0);
  const [animating, setAnimating] = useState(false);

  const ACTION_WIDTH = 72; // px per action
  const MAX_OFFSET = actions.length * ACTION_WIDTH;

  const snapTo = useCallback((x) => {
    setAnimating(true);
    setTranslateX(x);
    currentDelta.current = x;
    setTimeout(() => setAnimating(false), 180);
  }, []);

  const close = useCallback(() => snapTo(0), [snapTo]);

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setAnimating(false);
  };

  const handleTouchMove = (e) => {
    if (startX.current == null) return;
    const delta = e.touches[0].clientX - startX.current + currentDelta.current;
    // Only allow left swipe (negative translate), clamp to -MAX_OFFSET
    const clamped = Math.max(Math.min(delta, 0), -MAX_OFFSET);
    setTranslateX(clamped);
  };

  const handleTouchEnd = () => {
    if (startX.current == null) return;
    startX.current = null;
    // Snap to open if more than 40% revealed
    if (translateX < -MAX_OFFSET * 0.4) {
      snapTo(-MAX_OFFSET);
    } else {
      snapTo(0);
    }
  };

  // Close when another row opens or outside tap happens
  useEffect(() => {
    const onDocTouch = (e) => {
      if (!rowRef.current || rowRef.current.contains(e.target)) return;
      if (currentDelta.current < 0) close();
    };
    document.addEventListener('touchstart', onDocTouch);
    return () => document.removeEventListener('touchstart', onDocTouch);
  }, [close]);

  return (
    <div ref={rowRef} className="relative overflow-hidden" data-testid={testId}>
      {/* Action layer (revealed when swiped) */}
      <div
        className="absolute top-0 right-0 bottom-0 flex"
        aria-hidden={translateX === 0}
      >
        {actions.map((a, i) => {
          const Icon = a.icon;
          return (
            <button
              key={`${testId || 'swipe'}-action-${i}`}
              type="button"
              onClick={() => {
                a.onClick?.();
                close();
              }}
              data-testid={a.testId}
              className={`flex flex-col items-center justify-center gap-0.5 ${COLOR_CLASSES[a.color] || COLOR_CLASSES.primary}`}
              style={{ width: ACTION_WIDTH }}
              tabIndex={translateX === 0 ? -1 : 0}
            >
              {Icon && <Icon className="w-5 h-5" />}
              <span className="text-[10px] font-medium">{a.label}</span>
            </button>
          );
        })}
      </div>

      {/* Row content — translates on swipe */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: animating ? 'transform 180ms ease-out' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
};
